// ============================================================
// shared/aiSchemas.js — ce que les agents ont le DROIT de renvoyer.
// ------------------------------------------------------------
// Ces schémas servent deux fois, et c'est voulu :
//
//  1. ils sont convertis en JSON Schema et passés à l'API dans
//     `output_config.format`, donc le modèle est **contraint** à cette
//     forme au moment de générer ;
//  2. ils revalident la réponse côté serveur. La contrainte de sortie
//     n'est pas une garantie de confiance : un refus, une troncature
//     sur `max_tokens` ou une future évolution de l'API peuvent
//     produire autre chose. On ne fait jamais confiance à une sortie
//     de modèle sans la revalider.
//
// ── Ce qu'un agent n'a PAS le droit de renvoyer ──
//
// Il n'y a **aucun champ de prix** ici, et il n'y en aura jamais.
// Le prix est calculé par `shared/pricing.js`, point. De même, l'agent
// 1 ne renvoie ni brut ni volume : ce sont des calculs de
// `shared/geometry.js` à partir des cotes de la pièce FINIE.
//
// Tout champ absent du plan revient `null`. Une valeur inventée coûte
// plus cher qu'une valeur manquante : un `null` se voit à l'écran et
// se complète, une cote fausse part chez le client.
// ============================================================

import { z } from 'zod';
import {
  UNITES_GAMME,
  CATEGORIES_GAMME_COURANTES,
  UNITES_PAR_CATEGORIE,
} from './gamme.js';

/**
 * Nature d'une page. C'est le tri qui compte autant que l'extraction :
 * un mail client réel n'est pas « un plan = un PDF », c'est un seul
 * document qui mélange page de garde, plans et nomenclatures.
 *
 * Se tromper ici se PROPAGE — une page de garde prise pour un plan
 * crée une pièce fantôme dans un devis, une nomenclature prise pour un
 * plan produit des cotes inventées. C'est pour ça que le critère de
 * recette est de 100 % sur ce champ, et non sur l'extraction.
 */
export const PAGE_KINDS = [
  'plan', // dessin technique d'UNE pièce : c'est ce qui devient une pièce
  'cover', // page de garde, courrier, bon de commande
  'bom', // nomenclature, liste de pièces, tableau de quantités
  'assembly', // plan d'ENSEMBLE : plusieurs pièces, ne se chiffre pas tel quel
  'annex', // norme, spécification, cahier des charges, page de notes
  'blank', // page vide ou illisible
  'other', // rien de ce qui précède — force un arbitrage humain
];

export const CONFIDENCE_LEVELS = ['high', 'medium', 'low'];

/**
 * Orientation du CONTENU de la page, telle que le modèle la perçoit.
 *
 * Distincte de l'attribut `/Rotate` du PDF, qui est normalisé à 0 avant
 * l'envoi : ce champ décrit le scan lui-même. Une feuille posée de
 * travers sur la vitre reste de travers après normalisation, et c'est
 * précisément ce qu'on veut savoir.
 *
 * Il sert à deux choses : donner l'inventaire des pages réellement
 * inversées, et alimenter le niveau de confiance.
 */
export const PAGE_ORIENTATIONS = ['upright', 'rotated_90', 'rotated_180', 'rotated_270', 'unknown'];

/**
 * Champs qu'un plan peut porter sans qu'ils soient LISIBLES.
 *
 * `illegible_fields` sépare deux situations que `null` confondait, et
 * qui n'appellent pas du tout la même action :
 *
 *   null, absent de la liste  → le plan ne le dit pas. Le deviseur
 *                               décide (une pièce sans quantité, par
 *                               exemple : il la demande au client).
 *   null, PRÉSENT dans la liste → le plan le dit mais c'est illisible.
 *                               Quelqu'un doit regarder le papier.
 *
 * Cette distinction est née de l'arbitrage du 3 août 2026 : sur le
 * corpus Mertens, deux cases matière sont physiquement détruites — un
 * texte imprimé par-dessus le libellé. L'agent y avait rendu
 * « 39NiCrMo3 » et « C43 », deux INVENTIONS plausibles. Or une nuance
 * d'acier inventée fausse la densité, donc la masse, donc le prix — en
 * silence, et avec l'air d'être juste.
 *
 * D'où la règle : un caractère douteux rend le champ illisible, jamais
 * la valeur la plus proche.
 */
export const ILLEGIBLE_FIELDS = [
  'reference',
  'indice',
  'designation',
  'material_label',
  'material_norm',
  'quantity',
  'finished_shape',
  'surface_treatment',
];

/** Chaîne facultative : le modèle renvoie `null` quand l'info est absente. */
/**
 * Un texte venu de l'agent de lecture.
 *
 * Il n'arrive plus jamais `null` : le schéma d'API n'a plus un seul
 * champ nullable (voir `api/services/ai/planSchema.js`). Une chaîne vide
 * veut dire « absent ou illisible », et LEQUEL des deux se lit dans
 * `champs_absents` ou `illegible_fields`.
 *
 * On accepte quand même `null` à la lecture : les propositions déjà
 * enregistrées en base l'ont été sous l'ancien schéma, et une
 * revalidation qui les refuserait rendrait illisibles les devis passés.
 * On normalise vers `null` — c'est ce que le reste du programme attend.
 */
const text = (max) =>
  z
    .union([z.null(), z.string().trim().max(max)])
    .transform((v) => (v === '' ? null : v));

/**
 * Une COTE venue de l'agent de lecture.
 *
 * `0` désigne une cote qui ne s'applique pas — un Ø ou une profondeur de
 * 0 mm n'existent pas. On la remet à `null` ICI, à la frontière, pour
 * que le reste du programme ne voie jamais un zéro qui veut dire
 * « rien ». C'est la règle du §11 : un refus ne voyage pas dans la
 * valeur ; quand le transport l'y contraint, on le décode à l'entrée.
 */
const cote = () =>
  z
    .union([z.null(), z.number().finite()])
    .transform((v) => (v === 0 ? null : v));

const number = () => z.union([z.null(), z.number().finite()]);

/**
 * Une cote lue sur le plan, avec sa tolérance si elle est indiquée.
 * `label` est le texte tel qu'il figure sur le dessin — c'est ce qui
 * permet à un humain de retrouver la cote sur la feuille pour vérifier.
 */
export const planDimensionSchema = z
  .object({
    label: text(120),
    value_mm: number(),
    tolerance: text(60),
    /** Ce que la cote décrit, quand c'est identifiable. */
    axis: z.union([z.null(), z.enum(['length', 'width', 'height', 'diameter', 'thickness', 'other'])]),
  })
  .strict();

/**
 * Les formes de PIÈCE FINIE — perception, c'est ce qui est dessiné.
 *
 * On ne demande PAS la forme du brut : c'est une RÈGLE, et le code la
 * déduit (voir `shared/geometry.js`). Une règle est juste 100 % du temps
 * et s'explique ; un modèle interrogé là-dessus se trompe et n'explique
 * rien. On garde l'intelligence pour là où il n'y a pas de règle : les
 * prises et les temps.
 */
export const FINISHED_SHAPES = ['rotative', 'plate_disque', 'prismatique', 'complexe'];

/** Les types d'entité usinée, en énumération FERMÉE. */
export const ENTITY_TYPES = [
  'percage', 'percage_etage', 'lamage', 'fraisurage', 'taraudage', 'alesage',
  'rainure', 'chanfrein', 'gorge', 'filetage', 'surfacage', 'poche', 'fente',
];

/**
 * Le GROUPE de face — une position RELATIVE, pas absolue.
 *
 * Première version : `avant | arriere | pourtour | dessus | dessous |
 * inconnue`. Sur le plan SABCA, les sept entités sont sorties
 * `inconnue` — et l'agent avait raison : **rien sur ce plan ne dit où
 * est l'avant.** Une coupe C-C sans repère ne porte aucune information
 * absolue.
 *
 * Mais on n'a pas besoin de l'absolu. Ce qui décide du nombre de
 * prises, c'est **combien de côtés distincts portent des entités** —
 * et ça, une coupe le donne toujours. `A` et `B` sont deux côtés
 * opposés, sans dire lequel est l'avant ; `pourtour` est la
 * périphérie.
 *
 * Le gain est immédiat sur les notes : « côté opposé aux trous étagés »
 * énonce une RELATION, pas une position. Elle devient directement
 * exploitable — deux groupes, donc un retournement.
 */
/**
 * Le CÔTÉ d'un usinage — relatif, jamais absolu.
 *
 * `traversant` a été ajouté le 4 août 2026 pour une raison de calcul :
 * un alésage débouchant n'est sur AUCUN côté, il traverse A et B. Il
 * était classé « pourtour », ce qui est faux — le pourtour est la
 * périphérie — et surtout il comptait alors comme un côté distinct dans
 * le raisonnement sur les prises, donc il gonflait le nombre de
 * retournements et le prix.
 */
export const ENTITY_FACE_GROUPS = ['A', 'B', 'pourtour', 'traversant', 'inconnue'];

/** D'où vient le groupe : une vue du dessin, une note, ou nulle part. */
export const FACE_GROUP_SOURCES = ['vue', 'note', 'inconnue'];

/**
 * Une ENTITÉ usinée : comptée, dimensionnée, située.
 *
 * C'est le point le plus important du prompt de l'agent 1. La version
 * précédente rendait des MOTS (« Perçage ») ; celle-ci rend des DONNÉES
 * (« 4 × Ø6,5 débouchants, lamés Ø12, face avant »). Sans ça, rien ne
 * permet de comparer deux pièces, ni de calculer un volume de copeaux,
 * ni de raisonner sur un nombre de prises.
 */
export const planEntitySchema = z
  .object({
    type: z.enum(ENTITY_TYPES),
    /**
     * Combien d'exemplaires. C'est un COMPTE, pas une cote.
     *
     * Le modèle a rendu `0` sur quatre pages du corpus : il a appliqué à
     * un compte la convention « 0 = ne s'applique pas » qui vaut pour
     * les cotes. C'est notre faute — la consigne ne distinguait pas les
     * deux — et ça a coûté quatre pages ENTIÈRES, après quatre appels
     * payés.
     *
     * On ramène donc à 1 plutôt que de rejeter la page. Le compromis est
     * assumé et il n'est pas le même que sur une cote : une entité
     * listée existe au moins une fois, le nombre est AFFICHÉ à côté du
     * plan, et il se corrige en deux secondes. Un Ø faux, lui, fausserait
     * la masse en silence — d'où le traitement inverse.
     *
     * **Une page entière perdue pour un compte est un plus mauvais
     * échange qu'un compte à reprendre.**
     */
    nombre: z.number().int().min(0).max(500).transform((v) => (v === 0 ? 1 : v)),
    /**
     * Les cotes arrivent en NOMBRE, jamais en `null` — voir l'en-tête de
     * `api/services/ai/planSchema.js`. `0` désigne une cote qui ne
     * s'applique pas : un Ø ou une profondeur de 0 mm n'existent pas,
     * donc la valeur ne peut rien signifier d'autre.
     *
     * On la remet à `null` ICI, à la frontière, pour que le reste du
     * programme ne voie jamais un zéro qui veut dire « rien ». C'est la
     * règle du §11 : un refus ne voyage pas dans la valeur — et quand il
     * y est contraint par le transport, on le décode à l'entrée.
     */
    diametre_mm: cote(),
    diametre2_mm: cote(),
    profondeur_mm: cote(),
    longueur_mm: cote(),
    largeur_mm: cote(),
    pas_mm: cote(),
    angle_deg: cote(),
    /** Trois états : un booléen n'en a que deux, et « je ne sais pas »
     *  est une réponse légitime sur un plan photocopié. */
    traversant: z.enum(['traversant', 'borgne', 'inconnu']).default('inconnu'),
    tolerance: text(60),
    /** Côté RELATIF. `A` et `B` sont opposés, sans dire lequel est l'avant. */
    groupe_face: z.enum(ENTITY_FACE_GROUPS),
    groupe_face_source: z.enum(FACE_GROUP_SOURCES),
    /** La note qui a permis de trancher, citée. Chaîne vide sinon —
     *  la traçabilité ne coûte pas une union de plus. */
    groupe_face_note: z.string().trim().max(300).default(''),
    note: text(300),
  })
  .strict();

/** Une opération d'usinage lisible sur le plan. AUCUN temps ici :
 *  l'agent 1 lit, l'agent 2 propose les temps. */
export const planOperationSchema = z
  .object({
    /**
     * Le VERBE, choisi dans le catalogue. Pas une chaîne rédigée.
     *
     * L'ancien garde-fou était une liste noire — une contrainte `CHECK`
     * refusant les chiffres, les « × » et les « Ø ». Elle s'est fait
     * contourner au premier essai par « Tournage / surfaçage des deux
     * faces » : aucun caractère interdit, une quantité en toutes
     * lettres, et deux verbes mélangés. Une liste noire se contourne
     * toujours ; une liste blanche, non.
     *
     * La borne est vérifiée contre le catalogue RÉEL côté service : ce
     * schéma ne peut pas connaître les trente verbes de Mertens, ils
     * vivent en base et Charles les édite.
     */
    type: z.string().trim().min(1).max(80),
    /** Les chiffres de CETTE occurrence — jamais dans le verbe. */
    detail: text(200),
    /** La phrase du plan qui l'a fait retenir — traçabilité. */
    evidence: text(300),
  })
  .strict();

/**
 * Sortie de l'agent 1 pour UNE page.
 *
 * Quand `page_kind` n'est pas `plan`, tous les champs métier sont
 * attendus `null` : il n'y a pas de pièce à décrire sur une page de
 * garde. Le schéma ne l'impose pas (le modèle doit pouvoir signaler ce
 * qu'il voit), mais le service le vérifie et l'écrit dans les
 * avertissements.
 */
/**
 * ── `cotes_lues` NE PEUT PAS FAIRE ÉCHOUER UNE PAGE — 25 août 2026 ──
 *
 * Sur les devis 2026-08-055 et 2026-08-056, deux lectures ont échoué
 * ENTIÈREMENT — référence, matière, entités, tout perdu, l'appel payé —
 * pour ces deux raisons, et les deux sont des fautes de ce fichier :
 *
 *   055 : « Array must contain at most 20 element(s) ». J'avais écrit
 *         « 20 cotes suffisent largement pour un encombrement ». Ce
 *         chiffre n'a jamais été mesuré : c'est une borne inventée, et
 *         un plan classique la dépasse.
 *
 *   056 : « Number must be greater than 0 » sur `cotes_lues[3]`. Or le
 *         schéma de GÉNÉRATION dit lui-même, dans son helper `cote()` :
 *         « 0 si la cote ne s'applique pas ou n'est pas lisible ». Le
 *         modèle a suivi la convention de la maison, et la revalidation
 *         l'a refusée. J'avais mis les deux couches en contradiction.
 *
 * ── LA VRAIE FAUTE EST PLUS GRAVE QUE LES DEUX BORNES ──
 *
 * `cotes_lues` n'entre dans AUCUN calcul. C'est une preuve de lecture,
 * affichée à l'écran. Et une seule de ses lignes pouvait détruire toute
 * la page.
 *
 * > **Un champ qui n'entre dans aucun calcul ne doit jamais pouvoir
 * > invalider ce qui en entre.**
 *
 * C'est l'invariant 26 retourné contre moi : *un refus écarte LE
 * CANDIDAT, pas la cascade.* Mon contrôle écartait le candidat ET la
 * page entière.
 *
 * Chaque ligne est donc validée SEULE : une ligne fautive est écartée,
 * jamais la page. Un `0` est écarté parce qu'il ne porte aucune
 * information — c'est ce que la convention `cote()` en dit — et non
 * parce qu'il serait une erreur. La borne haute reste, mais elle
 * TRONQUE au lieu de refuser.
 */
const coteLueUnitaire = z
  .object({
    valeur_mm: z.coerce.number(),
    repere: z.string().trim().max(120).catch(''),
    evidence: z.string().trim().max(200).catch(''),
  })
  .strip();

/** Ce que le réel autorise : au-delà, ce ne sont plus des cotes d'encombrement. */
export const COTES_LUES_MAX = 80;

export const coteLueListe = z
  // AUCUNE borne qui REFUSE, à aucun niveau : c'est tout l'objet de ce
  // schéma. Le nombre d'éléments est borné par TRONCATURE, plus bas — et
  // de toute façon par les 16 000 jetons de sortie du modèle.
  //
  // `preprocess` couvre le dernier cas qui pouvait encore tuer la page :
  // le modèle rendant autre chose qu'un tableau. Il devient une liste
  // vide, et la lecture continue.
  .preprocess((v) => (Array.isArray(v) ? v : []), z.array(z.unknown()))
  .default([])
  .transform((brut) =>
    brut
      .map((c) => {
        const r = coteLueUnitaire.safeParse(c);
        return r.success ? r.data : null;
      })
      // `0` et les valeurs hors du réel sortent : une cote de 0 mm ou de
      // 40 m n'est pas une cote. Elles sortent SANS bruit parce qu'elles
      // ne portent rien — le bruit, ce serait de perdre la page avec.
      .filter((c) => c && Number.isFinite(c.valeur_mm) && c.valeur_mm > 0 && c.valeur_mm <= 10000)
      .slice(0, COTES_LUES_MAX));

export const planExtractionSchema = z
  .object({
    page_kind: z.enum(PAGE_KINDS),
    page_kind_reason: z.string().trim().min(1).max(800),
    page_orientation: z.enum(PAGE_ORIENTATIONS),

    /** Champs présents sur le plan mais que le modèle n'a pas pu lire.
     *  À distinguer d'un champ simplement absent : voir ILLEGIBLE_FIELDS. */
    illegible_fields: z.array(z.enum(ILLEGIBLE_FIELDS)).max(ILLEGIBLE_FIELDS.length),

    /**
     * Les champs que le plan ne PORTE PAS.
     *
     * Distincts des illisibles, et la distinction n'est pas cosmétique :
     * un champ illisible est une erreur de lecture qui compte dans la
     * fiabilité de l'agent, un champ absent est un plan qui ne dit rien
     * et ne prouve rien sur lui. Les compter ensemble ferait baisser une
     * note pour un papier muet.
     */
    champs_absents: z.array(z.enum(ILLEGIBLE_FIELDS)).max(ILLEGIBLE_FIELDS.length).default([]),

    reference: text(120),
    indice: text(20),
    designation: text(200),

    /** La désignation matière TELLE QU'ÉCRITE. Le rattachement au
     *  référentiel se fait côté serveur : l'agent ne décide pas d'une
     *  densité ni d'un prix au kilo. */
    material_label: text(120),

    /** `0` = le plan ne la porte pas. Une pièce à zéro exemplaire n'a
     *  pas de sens, donc 0 ne peut désigner qu'une absence. */
    quantity: z
      .union([z.null(), z.number().int().min(0)])
      .transform((v) => (v === 0 ? null : v)),

    /**
     * Forme perçue de la pièce FINIE. Le brut en est déduit par
     * `shared/geometry.js`, jamais demandé au modèle.
     *
     * L'énumération et les cotes d'encombrement DOIVENT être cohérentes :
     * le bug qui a motivé cette refonte vient de ce qu'on demandait
     * longueur / largeur / épaisseur sur un disque Ø75 × 7 — trois cotes
     * qui n'existent pas sur une rondelle.
     */
    finished_shape: z
      .union([z.null(), z.enum([...FINISHED_SHAPES, 'inconnue'])])
      .transform((v) => (v === 'inconnue' ? null : v)),

    /**
     * Encombrement, en une CHAÎNE au format strict — « D75 E7 »,
     * « L100 l50 E20 » — décodée par `parseEncombrement`.
     *
     * Pourquoi une chaîne plutôt que quatre nombres : l'API limite un
     * schéma à 16 paramètres à type d'union, et quatre cotes nullables
     * en consommaient quatre. Aucune information n'est perdue, le
     * décodage est déterministe et testé.
     */
    encombrement: z.string().trim().max(120).default(''),

    /**
     * ── CE QUI EST ÉCRIT SUR LE PAPIER NE SE JETTE PAS — v10 ──
     *
     * `encombrement` est un RANGEMENT dans trois cases ; il a le droit
     * d'être incomplet. `cotes_lues` est une PERCEPTION, et une
     * perception ne se perd jamais parce qu'on hésite sur son rôle.
     *
     * Le cas qui l'a fait écrire : sur « Bloc à souder », l'agent a lu
     * 35, 50, 50 et 13, n'a pas su laquelle était l'épaisseur, et a rendu
     * « L300 l50 ». Le brut est devenu incalculable sur une pièce
     * entièrement cotée.
     *
     * Bornes : 20 cotes suffisent largement pour un encombrement — au
     * delà, l'agent a listé les entités, qui ont leur propre champ. Et
     * 10 mètres est la borne du réel, pas du possible.
     */
    cotes_lues: coteLueListe,

    /** « Sheet 2/4 », « Foglio 1 Di 1 » — une pièce peut s'étaler sur
     *  plusieurs planches, et personne ne capturait ce champ. */
    planche: text(60),

    /** Les entités usinées, comptées et dimensionnées. */
    entites: z.array(planEntitySchema).max(120),

    critical_tolerances: z.array(text(1000)).max(60),
    surface_treatment: text(600),
    /** Les traitements RATTACHÉS au catalogue français. Le `type` est
     *  choisi dans un vocabulaire fermé injecté au modèle ; `libelle_lu`
     *  garde le texte du plan, dans sa langue, comme preuve — et comme
     *  futur alias. */
    traitements: z
      .array(
        z
          .object({
            type: z.string().trim().min(1).max(120),
            detail: z.string().trim().max(300).default(''),
            libelle_lu: z.string().trim().max(300).default(''),
          })
          .strict(),
      )
      .max(20)
      .default([]),
    /** Le canal du refus, comme pour les opérations. */
    traitements_non_classes: z.array(z.string().trim().max(200)).max(20).default([]),
    operations: z.array(planOperationSchema).max(60),
    /** Le canal du refus : un libellé lu qui ne correspond à aucun verbe
     *  du catalogue. Mieux vaut une ligne ici qu'un verbe forcé. */
    operations_non_classees: z.array(z.string().trim().max(200)).max(30).default([]),

    confidence: z.enum(CONFIDENCE_LEVELS),
    /** Ce que le modèle n'a pas pu lire. Un plan illisible doit le dire
     *  plutôt que de deviner. */
    unreadable: z.array(text(1000)).max(40),
  })
  .strict();

/**
 * Sortie de l'agent 2 pour UNE opération de la gamme.
 *
 * Toujours pas de prix : le prix indicatif est recalculé côté serveur
 * par `shared/pricing.js` à partir de ces temps.
 */
/**
 * La PRISE proposée par l'agent 2 — un montage sur une machine.
 *
 * `raison_existence` est le champ qui compte : sans lui, la réponse est
 * à prendre ou à laisser ; avec lui, elle est vérifiable en deux
 * secondes et corrigeable d'une façon qui apprend.
 */
export const PRISE_TYPES = ['montage_manuel', 'transfert_automatique'];

export const priseProposalSchema = z
  .object({
    machine_id: z.number().int().positive(),
    /**
     * Toutes les prises ne se valent pas, et c'est là que le NLX gagne
     * son argent.
     *
     * Un **transfert en contre-broche** est automatique : pas
     * d'opérateur, pas de démontage, pas de remise en position. Une
     * **reprise manuelle** est un réglage complet. Les compter pareil
     * dans « 2 prises » ferait mentir le nombre, et masquerait
     * exactement l'avantage qu'on cherche à mesurer.
     *
     * Le temps de réglage suit la NATURE des prises, pas leur nombre.
     */
    type: z.enum(PRISE_TYPES),
    entites_traitees: z.array(z.string().trim().max(300)).max(60),
    raison_existence: z.string().trim().min(1).max(1000),
    cycle_estime_min: z.number().min(0).finite(),
    reglage_estime_min: z.number().min(0).finite(),
  })
  .strict();

/**
 * Sortie de l'agent 2.
 *
 * Aucun champ nullable : un temps est un nombre, jamais `null`. « Je ne
 * sais pas » se dit dans `confiance` et `ce_qui_manque`, pas par un trou
 * dans un chiffre — un `null` se lirait comme zéro dans une addition.
 */
/**
 * Une LIGNE DE GAMME telle que l'agent la rend — revalidée à la
 * réception.
 *
 * `planSchema` contraint la forme au moment de générer ; ce schéma la
 * revérifie avec ses BORNES. On ne fait jamais confiance à une sortie de
 * modèle sans la revérifier — et un test compare les deux couches.
 */
export const ligneGammeProposalSchema = z
  .object({
    /**
     * LE VERBE de la ligne, et son identifiant au catalogue.
     *
     * `piece_gamme.operation_id` valait `null` sur les 164 lignes de la
     * base : rien ne reliait une ligne au catalogue, donc
     * l'apprentissage par verbe n'avait aucune clé de regroupement.
     *
     * Tolérant à l'absence : une proposition d'avant le prompt v12 n'en
     * porte pas, et elle doit rester rejouable.
     */
    operation: z.string().trim().max(120).nullable().optional(),
    operation_id: z.number().int().positive().nullable().optional(),
    op_num: z.number().int().min(0).max(100000),
    machine_id: z.union([z.number().int().positive(), z.null()]),
    libelle: z.string().trim().min(1).max(200),
    detail: z.string().trim().max(1000),
    entites: z.array(z.string().trim().max(200)).max(60),
    // Un temps NÉGATIF est une faute ; zéro est une valeur — un
    // transfert automatique peut coûter zéro minute.
    temps_min: z.number().min(0).finite(),
    unite: z.enum(UNITES_GAMME),
    // ── L'agent ne produit QUE le vocabulaire courant ──
    //
    // `CATEGORIES_GAMME` contient encore `cycle`, `montage` et
    // `programmation` : il le faut, pour relire les devis déjà chiffrés.
    // Mais un agent qui rendrait « programmation par projet » en 2027
    // fabriquerait une gamme d'un modèle abandonné, et personne ne le
    // verrait — le prix serait juste. On l'interdit donc À LA SORTIE,
    // pas seulement dans le prompt : une consigne de prompt est une
    // intention, un enum est une garantie.
    categorie: z.enum(CATEGORIES_GAMME_COURANTES),
    /** La part de PROGRAMMATION contenue dans « pièce 1 » — voir
     *  migration `1755930000000`. C'est elle, et elle seule, qu'une
     *  relance retire : le montage, lui, se refait à chaque lot. */
    programmation_incluse_min: z.union([z.number().min(0).finite(), z.null()]).default(null),
    justification: z.string().trim().max(600),
  })
  .strict()
  .superRefine((op, ctx) => {
    // Le MÊME couple (catégorie, unité) que la contrainte en base et que
    // `pieceGammeSchema`. Trois endroits disent la même chose, et c'est
    // voulu : la base fait foi, le schéma d'entrée nomme la faute, et
    // celui-ci l'attrape à la sortie du modèle — avant qu'elle atteigne
    // la moindre écriture.
    const autorisees = UNITES_PAR_CATEGORIE[op.categorie] ?? [];
    if (!autorisees.includes(op.unite)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['unite'],
        message: `OP ${op.op_num} : ${op.categorie} se compte en ${autorisees.join(' ou ')}.`,
      });
    }
  });

export const timesProposalSchema = z
  .object({
    /**
     * LA GAMME — et plus les trois nombres agrégés (§8.1).
     *
     * Les trois temps ne sont plus demandés : ils sont DÉRIVÉS par
     * `shared/gamme.js`. Les redemander laisserait deux vérités
     * coexister, et c'est toujours la mauvaise qui finit affichée.
     *
     * `min(1)` : une gamme vide n'est pas une estimation. Elle
     * dériverait trois temps à zéro, donc un prix nul, et rien ne le
     * signalerait — zéro converti, exactement.
     */
    gamme: z.array(ligneGammeProposalSchema).min(1).max(60),
    /**
     * LA FORME DU BRUT, CHOISIE PAR L'AGENT — 27 août 2026.
     *
     * Revalidée ici comme tout le reste : on ne fait jamais confiance à
     * une sortie de modèle sans la revérifier, même contrainte par un
     * schéma d'API.
     *
     * `.optional()` et pas `.min(1)` : une estimation d'avant le
     * prompt v12 n'en porte pas, et une proposition rejouée depuis
     * `ai_propositions` doit rester lisible. Son absence fait retomber
     * sur la déduction du code — c'est le comportement d'avant, et il
     * reste juste.
     */
    brut: z
      .object({
        forme: z.enum(['barreau', 'barre_ronde', 'barre_ronde_prismatique', 'a_la_cote']),
        raison: z.string().trim().max(500).nullable().optional(),
      })
      .optional()
      .nullable(),
    regles_utilisees: z.array(z.number().int().positive()).max(50),
    corrections_utilisees: z.array(z.number().int().positive()).max(50),
    confiance: z.enum(CONFIDENCE_LEVELS),
    ce_qui_manque: z.array(z.string().trim().max(500)).max(20),
    raisonnement: z.string().trim().max(4000),
    /**
     * Une justification par temps, à côté de la valeur, pas dans un
     * paragraphe global.
     *
     * Le raisonnement d'ensemble existe déjà et il ne suffit pas : il
     * est lu une fois, il n'est rattaché à aucun nombre, et il ne dit
     * pas lequel des trois temps il défend. Un deviseur qui trouve le
     * cycle trop long veut savoir ce qu'on y a mis — pas relire le fil
     * d'une décision.
     *
     * `min(1)` : une justification vide fait échouer la validation.
     * C'est voulu. Un nombre sans phrase est un nombre qu'on ne peut ni
     * vérifier ni corriger utilement.
     */
    justification_cycle: z.string().trim().min(1).max(1200),
    justification_reglage: z.string().trim().min(1).max(1200),
    justification_programmation: z.string().trim().min(1).max(1200),
  })
  .strict();

/**
 * Décode l'encombrement rendu par l'agent 1.
 *
 * Format attendu, et rien d'autre — une suite de repères collés à leur
 * valeur, séparés par des espaces :
 *   rotative      « D40 L120 »
 *   plate/disque  « D75 E7 »
 *   prismatique   « L100 l50 E20 »
 *
 * **L majuscule est la longueur, l minuscule la largeur.** La casse
 * compte, et les confondre donnerait un volume faux.
 *
 * ── Pourquoi il SIGNALE au lieu de rendre null ──
 *
 * La première version rendait `null` sur tout ce qu'elle ne comprenait
 * pas. « abc », « 150 x 56 » et « DD150 » donnaient exactement le même
 * résultat que la chaîne vide : une cote absente était indiscernable
 * d'une cote NON LUE. C'est précisément le défaut qu'`illegible_fields`
 * avait supprimé côté agent, réintroduit côté décodeur.
 *
 * Désormais : tout fragment non reconnu est rendu dans `probleme`, et
 * `ok` vaut `false`. La confiance calculée en tient compte et fait
 * tomber la page en `low` — c'est un signal de relecture, pas une
 * valeur silencieusement perdue.
 *
 * La chaîne VIDE est un cas à part et légitime : le prompt demande
 * explicitement de rendre une chaîne vide quand rien n'est lisible.
 * Elle rend `ok: true` et `vide: true`.
 *
 * @returns {{cotes, ok, vide, probleme, reconnus}}
 */
export function parseEncombrement(texte) {
  const cotes = { diametre_mm: null, longueur_mm: null, largeur_mm: null, epaisseur_mm: null };
  const brut = String(texte ?? '').trim();

  if (!brut) {
    return { cotes, ok: true, vide: true, probleme: null, reconnus: [] };
  }

  const CHAMP = { D: 'diametre_mm', E: 'epaisseur_mm', L: 'longueur_mm', l: 'largeur_mm' };
  const jetons = brut.split(/\s+/);
  const reconnus = [];
  const refuses = [];

  for (const jeton of jetons) {
    // Un repère (D, E, L majuscule ou l minuscule) suivi d'un nombre
    // strictement positif, et RIEN d'autre autour.
    const m = jeton.match(/^([DELl])([0-9]+(?:[.,][0-9]+)?)$/);
    if (!m) {
      refuses.push(jeton);
      continue;
    }
    // `D` et `E` sont sans ambiguïté ; `L` et `l` se distinguent par la
    // casse, et le motif l'a déjà respectée.
    const champ = CHAMP[m[1]];
    const valeur = Number(String(m[2]).replace(',', '.'));
    if (!Number.isFinite(valeur) || valeur <= 0) {
      refuses.push(jeton);
      continue;
    }
    if (cotes[champ] !== null) {
      // Deux fois la même cote : on ne choisit pas laquelle est la
      // bonne, on le signale.
      refuses.push(jeton);
      continue;
    }
    cotes[champ] = valeur;
    reconnus.push(jeton);
  }

  return {
    cotes,
    ok: refuses.length === 0 && reconnus.length > 0,
    vide: false,
    probleme: refuses.length
      ? `Encombrement non décodable : ${refuses.join(', ')}. Format attendu « D75 E7 » ou « L100 l50 E20 ».`
      : reconnus.length === 0
        ? 'Encombrement présent mais aucune cote reconnue.'
        : null,
    reconnus,
  };
}

// ============================================================
// LA FICHE D'ATELIER LUE SUR UNE PHOTO — seconde couche.
// ------------------------------------------------------------
// `ficheOutputFormat` (api/services/ai/ficheReader.js) contraint la
// GÉNÉRATION ; ce schéma valide la RÉCEPTION. On ne fait jamais
// confiance à une sortie de modèle sans la revérifier.
//
// Les bornes sont celles du réel, pas celles du possible : un temps
// d'opération de plus de 100 000 minutes — soixante-dix jours — n'est
// pas un temps d'usinage, c'est une lecture ratée. Elles sont les mêmes
// que celles du formulaire du téléphone, et c'est délibéré : deux
// canaux qui écrivent dans la même colonne doivent accepter la même
// chose, sinon la valeur qui passe dépend du chemin qu'elle a pris.
// ============================================================
export const ficheOperationSchema = z
  .object({
    op_num: z.number().int(),
    temps_reel_min: z.number().min(0).max(100000).nullable(),
  })
  .strict();

export const ficheLueSchema = z
  .object({
    est_une_fiche: z.boolean(),
    // La forme AAMMJJ-NNN n'est PAS imposée ici, et c'est volontaire :
    // le modèle doit pouvoir rendre ce qu'il a lu même quand c'est
    // déformé, pour que l'écran affiche « lu 260810-O03, aucune pièce
    // ne porte cette référence ». Un schéma qui refuserait la valeur
    // effacerait l'indice qui permet de comprendre l'échec.
    reference_interne: z.string().trim().max(64).nullable(),
    operateur: z.string().trim().max(120).nullable(),
    pieces_bonnes: z.number().int().min(0).max(1000000).nullable(),
    rebuts: z.number().int().min(0).max(1000000).nullable(),
    operations: z.array(ficheOperationSchema).max(200),
    champs_illisibles: z.array(z.string().max(120)).max(50),
  })
  .strict();
